import { useState } from 'react';
import YardFilter from '../components/TimeWageAudit/YardFilter';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import {
  AlertTriangle, XCircle, Clock, Users, Info, Download, LogIn, LogOut,
} from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import { fmtNumber, fmtDate, fmtTimePT } from '../components/TimeWageAudit/format';
import DriverTimeline from '../components/TimeWageAudit/DriverTimeline';
import ShiftTimelineList from '../components/TimeWageAudit/ShiftTimelineList';
import SegmentPopover from '../components/TimeWageAudit/SegmentPopover';
import TimelineLegend from '../components/TimeWageAudit/TimelineLegend';
import PageHeader from '../components/Shared/PageHeader';
import { btn, card, tbl } from '../theme';

// Mirrors exactly what the shift list shows — one row per shift, with the
// meal gaps flattened into a summary rather than dropped, so the spreadsheet
// answers the same question the screen does.
//
// Scoped to the CURRENT PAGE and labelled that way on the button. An export
// that silently ships 50 of 1,776 rows is the failure this module already has
// on its meal list; not repeating it here.
function exportShiftsToExcel(rows) {
  const data = rows.map((r) => ({
    'Employee ID':      r.employee_id,
    'Worker Name':      r.worker_name || '',
    'Shift Date':       fmtDate(r.shift_date),
    'Job Profile':      r.job_profile || '',
    'Cost Center':      r.cost_center_code || '',
    'Supervisor':       r.supervisor || '',
    'Shift Start (PT)': fmtTimePT(r.shift_start),
    'Shift End (PT)':   fmtTimePT(r.shift_end),
    'Hours Worked':     r.worked_minutes == null ? '' : (Number(r.worked_minutes) / 60).toFixed(2),
    // Pre-trip first: the columns run in the order the shift happens, so a
    // reader scanning left to right walks the shift rather than jumping ends.
    'Departure Yard':      r.departure_yard_name || '',
    'Pre-Trip Submitted (PT)': fmtTimePT(r.pre_inspection_submitted),
    'Pre-Trip Minutes':    r.pre_inspection_minutes ?? '',
    'Left Yard (PT)':      fmtTimePT(r.yard_departure),
    'Clock-in to Departure Minutes': r.clockin_to_departure_min ?? '',
    'Pre-Trip Unaccounted Minutes':  r.pre_unaccounted_min ?? '',
    'Pre-Trip Category':   r.pre_category || '',
    'Pre-Trip Completeness': r.pre_completeness || '',
    'Yard':             r.yard_name || '',
    'Back at Yard (PT)':  fmtTimePT(r.yard_arrival),
    'Inspection Submitted (PT)': fmtTimePT(r.inspection_submitted),
    'Inspection Minutes': r.inspection_minutes ?? '',
    'Unaccounted Minutes': r.unaccounted_min ?? '',
    'Close-out Category':  r.category || '',
    'Data Completeness':   r.completeness || '',
    'Has Shift Record':    r.has_shift_record ? 'yes' : 'no',
    'Meal Gaps':           (r.meals || []).length,
    'Meal Gap Minutes':    (r.meals || []).reduce((n, m) => n + (Number(m.gap_minutes) || 0), 0) || '',
    'Meal Gap Windows (PT)': (r.meals || [])
      .map((m) => `${fmtTimePT(m.gap_start)}-${fmtTimePT(m.gap_end)}`).join('; '),
  }));
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Shifts');
  XLSX.writeFile(wb, `time-wage-audit-shifts-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export default function TimeWageAudit() {
  const qc = useQueryClient();

  // The unit of this page is a SHIFT, not a finding. Meal discrepancies are a
  // filter over that list rather than the list itself — a gap reads
  // differently inside a fourteen-hour shift than inside a six-hour one, and
  // the finding-first view could never show which it was.
  // A LIST of yards; empty means all, which is the same thing the server means
  // by an absent parameter. No separate "all" flag to disagree with the list.
  const [ccFilter, setCcFilter]   = useState([]);
  // One day, not a range. The two ends were almost always set to the same
  // value in practice, and a half-set range — From with no To — quietly means
  // "everything after this date", which is a different and much larger
  // question than the one the reader thought they asked. Empty still means
  // the server's default: the most recent day with data.
  const [date, setDate]           = useState('');
  const [hasMealGap, setHasMealGap]     = useState(false);
  const [overThreshold, setOverThreshold] = useState(false);
  const [preOverThreshold, setPreOverThreshold] = useState(false);
  const [driverQ, setDriverQ]     = useState('');
  // Defaults to flagged-first. See the ordering note in routes/timeWageAudit.js
  // for why alphabetical is the wrong thing to open on.
  const [sort, setSort]           = useState('flagged');
  const [page, setPage]           = useState(1);
  const [selected, setSelected]   = useState(null);   // clicked timeline segment

  // Cost-center options still come from the meal summary, which is the only
  // endpoint that already knows the full set with counts.
  const { data: summary } = useQuery({
    queryKey: ['twa-summary'],
    queryFn:  () => api.get('/time-wage-audit/summary'),
  });

  const shiftParams = {
    ...(ccFilter.length ? { cost_center_id: ccFilter.join(',') } : {}),
    ...(date ? { date_from: date, date_to: date } : {}),
    ...(driverQ.trim() ? { q: driverQ.trim() } : {}),
    ...(hasMealGap ? { has_meal_gap: 'true' } : {}),
    ...(overThreshold ? { over_threshold: 'true' } : {}),
    ...(preOverThreshold ? { pre_over_threshold: 'true' } : {}),
    ...(sort !== 'flagged' ? { sort } : {}),
    page,
    limit: 50,
  };

  const { data: shiftData, isLoading: shiftsLoading } = useQuery({
    queryKey: ['twa-shifts', shiftParams],
    queryFn:  () => api.get('/time-wage-audit/shifts', { params: shiftParams }),
  });

  const { data: shiftSummary, isLoading: summaryLoading } = useQuery({
    queryKey: ['twa-shift-summary', date],
    queryFn:  () => api.get('/time-wage-audit/shifts/summary', {
      // The tiles MUST take the same window as the list, or the board reads
      // 18,513 shifts above a list of 1,776 — the divergence resolveWindow
      // exists to prevent, reintroduced from the client side.
      params: date ? { date_from: date, date_to: date } : {},
    }),
  });

  // Driver drill-down: the same driver's whole history, which is a different
  // question from "what happened on this shift".
  const [timelineFor, setTimelineFor] = useState(null);

  const { data: timeline } = useQuery({
    queryKey: ['twa-timeline', timelineFor, date],
    queryFn:  () => api.get(`/time-wage-audit/timeline/${timelineFor}`, {
      // Deliberately NOT narrowed to the selected day: the drill-down answers
      // "what does this driver's history look like", which a single day cannot.
      params: {},
    }),
    enabled: !!timelineFor,
  });

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ['twa-summary'] });
    qc.invalidateQueries({ queryKey: ['twa-shifts'] });
    qc.invalidateQueries({ queryKey: ['twa-shift-summary'] });
    qc.invalidateQueries({ queryKey: ['twa-timeline'] });
  }

  // Any filter change invalidates the current page number — page 7 of the old
  // result set is meaningless against the new one, and silently showing its
  // rows is worse than resetting.
  function withReset(setter) {
    return (v) => { setPage(1); setter(v); };
  }

  if (summaryLoading) return <LoadingSpinner />;
  const kpis    = shiftSummary?.kpis || {};
  const shifts  = shiftData?.shifts || [];
  const total   = shiftData?.total || 0;
  const limit   = shiftData?.limit || 50;
  const pages   = Math.max(Math.ceil(total / limit), 1);
  // One date, because the filter is one day. The arrow form said
  // "2026-08-20 → 2026-08-20" on every load, which reads as a range the reader
  // then has to check is not one. The range form is kept only for the case
  // where the server answers with a window we did not ask for — it should not
  // happen from this page, and if it ever does, saying so is better than
  // silently showing one end of it.
  const wFrom = shiftData ? String(shiftData.date_from).slice(0, 10) : '';
  const wTo   = shiftData ? String(shiftData.date_to).slice(0, 10) : '';
  const window_ = !shiftData ? ''
    : wFrom === wTo ? fmtDate(wFrom)
    : `${fmtDate(wFrom)} → ${fmtDate(wTo)}`;

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Compliance"
        title="Time and Wage Audit"
        subtitle="Driver shifts from Workday punches, Samsara vehicle movement and Fleetio inspections, on one time axis. Meal-break discrepancies and long close-outs appear as segments within the shift they belong to — and as filters over this list. Per the source system, a discrepancy between two systems is not an adjudicated violation: every record needs human review before any action is taken."
      />

      <div style={s.kpiRow}>
        <KPICard icon={<Clock size={16} />} label="Shifts in view" value={fmtNumber(kpis.total_shifts)} />
        <KPICard icon={<AlertTriangle size={16} />} label="With meal discrepancy" value={fmtNumber(kpis.shifts_with_meal_gap)} accent="#DC2626" />
        <KPICard icon={<LogOut size={16} />} label={`Close-out over ${shiftSummary?.threshold_min ?? 45}m`} value={fmtNumber(kpis.over_threshold)} />
        <KPICard icon={<LogIn size={16} />} label={`Long start over ${shiftSummary?.pre_threshold_min ?? 60}m`} value={fmtNumber(kpis.pre_over_threshold)} />
        <KPICard icon={<XCircle size={16} />} label="Out before return" value={fmtNumber(kpis.out_before_return)} />
        <KPICard icon={<Users size={16} />} label="Drivers" value={fmtNumber(kpis.drivers)} />
      </div>

      <div style={s.tierNote}>
        <Info size={14} color="#1D4ED8" style={{ flexShrink: 0, marginTop: '1px' }} />
        <span>
          {/* Coverage stated up front rather than discovered. The two feeds do
              not span the same window, and a reader who assumes they do will
              read an absence as a clean shift. */}
          {fmtNumber(kpis.measured_shifts)} of {fmtNumber(kpis.total_shifts)} shifts have a measurable
          close-out and {fmtNumber(kpis.pre_measured_shifts)} a measurable start; the rest have a punch
          but no usable yard or inspection data at that end, so it reads blank rather than zero. The
          two ends are measured independently — a shift can be blank at one and complete at the other,
          and the front of the shift is the less observable of the two.
          {/* The blank that looks most like a bug in this page: the inspection
              is there, so "no close-out" reads as something we failed to draw.
              It is a source gap with a nameable cause, and stating the size of
              it here stops every reader rediscovering it one shift at a time. */}
          {Number(kpis.inspected_no_arrival) > 0 && <>
            {' '}<strong>{fmtNumber(kpis.inspected_no_arrival)}</strong> of those did submit an
            inspection but could not be matched to a yard arrival — Fleetio records the inspection
            against a vehicle, Samsara supplies the arrival, and where the two do not resolve to the
            same vehicle there is nothing to measure from.
            {Number(kpis.inspected_no_arrival_multiform) > 0 && <>
              {' '}{fmtNumber(kpis.inspected_no_arrival_multiform)} of them submitted more than one
              inspection, the usual shape for a tractor and trailer.
            </>}
          </>}
          {Number(kpis.shifts_without_record) > 0 && <>
            {' '}A further {fmtNumber(kpis.shifts_without_record)} day{Number(kpis.shifts_without_record) === 1 ? '' : 's'} in
            this range carry meal gaps with <strong>no shift record at all</strong> — the post-trip feed
            does not cover them. They appear as a dashed line, not a bar.
          </>}
        </span>
      </div>

      <div style={s.filterRow}>
        <div style={s.filterGroup}>
          <label style={s.label}>Yards</label>
          <YardFilter
            options={(summary?.by_cost_center || [])
              .filter((r) => r.cc_id)
              .map((r) => ({ id: r.cc_id, code: r.code, name: r.name }))}
            selected={ccFilter}
            onChange={withReset(setCcFilter)}
          />
        </div>
        <div style={s.filterGroup}>
          <label style={s.label}>Date</label>
          <input type="date" id="twa-date" style={s.select} value={date}
                 onChange={(e) => withReset(setDate)(e.target.value)} />
        </div>
        <div style={s.filterGroup}>
          <label style={s.label}>Driver</label>
          <input type="search" placeholder="name or ID" style={s.select} value={driverQ}
                 onChange={(e) => withReset(setDriverQ)(e.target.value)} />
        </div>
        <div style={s.filterGroup}>
          <label style={s.label}>Sort</label>
          <select id="twa-sort" style={s.select} value={sort} onChange={(e) => withReset(setSort)(e.target.value)}>
            <option value="flagged">Needs review first</option>
            <option value="name">Name (A–Z)</option>
          </select>
        </div>
        {/* The three filters travel as one block. Left loose in the row they
            broke across two lines at the width the page actually renders at,
            splitting one question — which shifts do I want? — across a line
            break, with the selects above claiming the first line. */}
        <div style={s.checkGroup}>
        <label style={s.checkLabel}>
          <input type="checkbox" checked={hasMealGap}
                 onChange={(e) => withReset(setHasMealGap)(e.target.checked)} />
          Only meal discrepancies
        </label>
        <label style={s.checkLabel}>
          <input type="checkbox" checked={overThreshold}
                 onChange={(e) => withReset(setOverThreshold)(e.target.checked)} />
          Only long close-outs
        </label>
        {/* Ticking both narrows to shifts that ran long at BOTH ends — 7 of
            18,811 on the profiled export — rather than either. That is the
            more interesting question and falls out of two checkboxes for
            free, which a single-select control could not express. */}
        <label style={s.checkLabel}>
          <input type="checkbox" checked={preOverThreshold}
                 onChange={(e) => withReset(setPreOverThreshold)(e.target.checked)} />
          Only long starts
        </label>
        </div>
      </div>

      {timelineFor && timeline && (
        <DriverTimeline data={timeline} onClose={() => setTimelineFor(null)} />
      )}

      <div style={s.card}>
        <div style={s.tableHeaderBar}>
          <span style={s.tableHeaderTitle}>
            {fmtNumber(total)} shift{total === 1 ? '' : 's'}
            <span style={s.windowNote}>{window_}</span>
          </span>
          <button
            style={{ ...s.exportBtn, opacity: shifts.length ? 1 : 0.5 }}
            disabled={!shifts.length}
            onClick={() => exportShiftsToExcel(shifts)}
          >
            <Download size={13} /> Export this page
          </button>
        </div>

        <TimelineLegend
          thresholdMin={shiftData?.threshold_min ?? 45}
          preThresholdMin={shiftData?.pre_threshold_min ?? 60}
        />

        {shiftsLoading ? <LoadingSpinner /> : (
          <div style={{ padding: `0 18px 12px` }}>
            <ShiftTimelineList
              shifts={shifts}
              axis={shiftSummary?.axis || null}
              thresholdMin={shiftData?.threshold_min ?? 45}
              preThresholdMin={shiftData?.pre_threshold_min ?? 60}
              selectedKey={selected?.key}
              onSelectSegment={setSelected}
              onSelectDriver={setTimelineFor}
            />
          </div>
        )}

        {pages > 1 && (
          <div style={s.pager}>
            <button style={s.pageBtn} disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
            {/* The total is stated, not implied. A page that shows 50 of 1,776
                with no count reads as "these are all of them". */}
            <span style={s.pageInfo}>
              Page {page} of {fmtNumber(pages)} · {fmtNumber(total)} shifts
            </span>
            <button style={s.pageBtn} disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>

      {/* Anchored to the segment that was clicked rather than rendered in a
          fixed slot — on a 50-row list the old placement meant scrolling away
          to read the answer and back to click the next one. */}
      {selected && (
        <SegmentPopover
          selected={selected}
          thresholdMin={shiftData?.threshold_min ?? 45}
          preThresholdMin={shiftData?.pre_threshold_min ?? 60}
          onClose={() => setSelected(null)}
          onReviewed={invalidateAll}
        />
      )}
    </div>
  );
}

function KPICard({ icon, label, value, accent }) {
  return (
    <div style={s.kpiCard}>
      <div style={s.kpiIcon}>{icon}</div>
      <div>
        <div style={{ ...s.kpiValue, ...(accent ? { color: accent } : {}) }}>{value}</div>
        <div style={s.kpiLabel}>{label}</div>
      </div>
    </div>
  );
}

const s = {
  page:        { maxWidth: '1200px', margin: '0 auto' },
  hd2:         { marginBottom: '16px' },
  title2:      { fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '4px' },
  sub:         { fontSize: '13px', color: '#6B7280', lineHeight: 1.6 },
  kpiRow:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '14px' },
  kpiCard:     { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '16px 18px', display: 'flex', alignItems: 'center', gap: '12px' },
  kpiIcon:     { width: '34px', height: '34px', borderRadius: '8px', background: '#EFF6FF', color: '#1D4ED8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  kpiValue:    { fontSize: '18px', fontWeight: 700, color: '#111827' },
  kpiLabel:    { fontSize: '11px', color: '#6B7280', marginTop: '1px' },
  tierNote: {
    display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '10px 14px', marginBottom: '18px',
    background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '10px', fontSize: '12px', color: '#1E3A5F', lineHeight: 1.6,
  },
  filterRow:   { display: 'flex', alignItems: 'flex-end', gap: '14px', marginBottom: '16px', flexWrap: 'wrap' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label:       { fontSize: '11px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' },
  select:      { padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', color: '#374151', fontFamily: 'inherit', minWidth: '200px' },
  checkGroup:  { display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'nowrap' },
  checkLabel:  { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151', paddingBottom: '7px', whiteSpace: 'nowrap' },
  card:        { ...card.base, overflow: 'hidden', marginBottom: '24px' },
  feedTabs:    { display: 'flex', gap: '6px', marginBottom: '12px' },
  feedTab:     { ...btn.secondarySm },
  feedTabOn:   { background: '#1B2A4A', color: '#E8EEF7', borderColor: '#1B2A4A' },
  breakdown:   { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '10px' },
  breakdownLabel: { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.12em', color: '#9CA3AF', marginRight: '2px' },
  breakdownChip:  { fontSize: '11px', color: '#374151', background: '#F3F4F6',
                    border: '1px solid #E5E7EB', borderRadius: '999px', padding: '2px 8px',
                    fontVariantNumeric: 'tabular-nums' },
  windowNote:  { marginLeft: '10px', fontSize: '12px', fontWeight: 400, color: '#6B7280' },
  pager:       { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px',
                 padding: '12px 18px', borderTop: '1px solid #E5E7EB', background: '#F9FAFB' },
  pageBtn:     { ...btn.secondarySm },
  pageInfo:    { fontSize: '12px', color: '#6B7280', fontVariantNumeric: 'tabular-nums' },
  detailCard:  { ...card.base, padding: '18px', marginBottom: '24px', background: '#F9FAFB' },
  detailHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  detailTitle: { fontSize: '15px', fontWeight: 650, color: '#111827' },
  detailClose: { ...btn.icon },
  detailNote:  { fontSize: '12px', color: '#374151', lineHeight: 1.5, marginTop: '12px',
                 paddingTop: '12px', borderTop: '1px solid #E5E7EB' },
  kvGrid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' },
  kvK:         { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em',
                 color: '#9CA3AF', marginBottom: '3px' },
  kvV:         { fontSize: '13px', fontVariantNumeric: 'tabular-nums' },
  driverLink:  { ...btn.link, fontWeight: 650, textAlign: 'left' },
  cardTitle:   { ...card.head },
  tableHeaderBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB' },
  tableHeaderTitle: { fontSize: '13px', fontWeight: 600, color: '#374151' },
  exportBtn: { ...btn.secondarySm },
  tableScroll: { overflowX: 'auto' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:          { ...tbl.th, ...tbl.th, whiteSpace: 'nowrap' },
  tr:          { ...tbl.tr, ...tbl.tr },
  td:          { ...tbl.td, ...tbl.td, whiteSpace: 'nowrap' },
  tdR:         { ...tbl.tdR, ...tbl.tdR },
  empty:       { textAlign: 'center', padding: '24px', color: '#9CA3AF' },
  pill:        { display: 'inline-flex', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600, textTransform: 'capitalize' },
  clearTag:    { fontSize: '9px', fontWeight: 700, color: '#B45309', background: '#FEF3C7', padding: '1px 5px', borderRadius: '4px', marginLeft: '4px' },
  reviewToggleBtn: { ...btn.secondaryXs },
  metaRow:     { borderBottom: '1px solid #F3F4F6' },
  metaTd:      { padding: '2px 12px 10px', background: '#FCFCFD' },
  metaChips:   { display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', fontSize: '11px', color: '#6B7280' },
  punchOriginTag: { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: '#92400E', background: '#FEF3C7', padding: '2px 8px', borderRadius: '10px' },
  punchOriginTagNeutral: { color: '#6B7280', background: '#F3F4F6' },
  evidenceLink: { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: '#1D4ED8', textDecoration: 'none' },
  editRow:     { borderBottom: '1px solid #F3F4F6', background: '#F9FAFB' },
  editTd:      { padding: '16px' },
  textarea:    { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', color: '#374151', fontFamily: 'inherit', resize: 'vertical', marginTop: '5px' },
  editActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '10px' },
  resetBtn:    { ...btn.secondarySm },
  dismissBtn:  { padding: '8px 16px', fontSize: '12px', fontWeight: 600, border: 'none', borderRadius: '6px', background: '#16A34A', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' },
  confirmReviewBtn: { padding: '8px 16px', fontSize: '12px', fontWeight: 600, border: 'none', borderRadius: '6px', background: '#B91C1C', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' },
  reviewMeta:  { fontSize: '11px', color: '#9CA3AF', marginTop: '8px' },
  ingestSection: { marginTop: '8px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' },
  dropZone: {
    border: '2px dashed', borderRadius: '10px', padding: '32px', textAlign: 'center', cursor: 'pointer',
    marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', transition: 'border-color 0.2s',
  },
  dropText:     { fontSize: '13px', color: '#374151', lineHeight: 1.6 },
  progressWrap: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
  progressTrack:{ flex: 1, height: '5px', background: '#E5E7EB', borderRadius: '4px', overflow: 'hidden' },
  progressFill: { height: '100%', background: '#1B2A4A', borderRadius: '4px', transition: 'width 0.2s' },
  progressLabel:{ fontSize: '11px', fontWeight: 600, color: '#374151', minWidth: '30px' },
  uploadBtn: { ...btn.primary, transition: 'opacity 0.15s' },
  previewGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', padding: '20px', paddingBottom: '0' },
  previewStat: { textAlign: 'center' },
  previewStatValue: { fontSize: '20px', fontWeight: 700 },
  previewStatLabel: { fontSize: '11px', color: '#6B7280', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  warningBox: {
    display: 'flex', alignItems: 'flex-start', gap: '8px', margin: '16px 20px 0',
    padding: '10px 12px', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '8px', fontSize: '12px', color: '#92400E', lineHeight: 1.5,
  },
  previewActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '20px' },
  cancelBtn: { ...btn.secondarySm },
  syncBtn:   { ...btn.primarySm },
  logSection:  { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' },
  logTitle:    { fontSize: '13px', fontWeight: 600, color: '#374151', padding: '14px 18px', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB' },
  logTable:    { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  logTh:       { ...tbl.th, ...tbl.th },
  logTr:       { borderBottom: '1px solid #F3F4F6' },
  logTd:       { ...tbl.td, ...tbl.td },
  statusPill:  { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 500 },
  emptyLog:    { textAlign: 'center', padding: '24px', color: '#9CA3AF' },
};
